import { useEffect, useRef } from 'react';
import Head from 'next/head';
import { noop } from 'lodash-es';

import { Box, chakra } from '@chakra-ui/react';

export function WaveVideoBackground() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;

    function videoPlay() {
      if (video) {
        video
          .play()
          .catch(noop)
          .finally(() => {
            window.removeEventListener('mousemove', videoPlay);
          });
      }
    }

    if (!video) return;
    // Note: this effect is an optimization to improve Lighthouse scores.
    // We don't really need the video to load until after the page has loaded since we have a poster to render in place of the video.
    for (const child of video.children) {
      const videoSource = child as HTMLSourceElement;
      if (typeof videoSource.tagName === 'string' && videoSource.tagName === 'SOURCE') {
        videoSource.src = videoSource.dataset.src as string;
      }
    }

    video.load();

    window.addEventListener('mousemove', videoPlay);
    return () => {
      window.removeEventListener('mousemove', videoPlay);
    };
  }, []);

  const pathToPoster = '/videos/waves-poster.webp';

  return (
    <>
      <Head>
        <link key='wave-poster' as='image' fetchPriority='high' href={pathToPoster} rel='preload' type='image/webp' />
      </Head>
      <Box bottom='0px' height='100%' left='50%' position='absolute' transform='translateX(-50%)' width='100%'>
        <chakra.video
          ref={videoRef}
          height='100%'
          inset={0}
          objectFit='cover'
          pointerEvents='none'
          position='absolute'
          poster={pathToPoster}
          preload='none'
          width='100%'
          sx={{
            '&::-webkit-media-controls-start-playback-button': {
              display: 'none !important',
            },
          }}
          loop
          muted
          playsInline
        >
          <source data-src='/videos/waves.webm' type='video/webm' />
          <source data-src='/videos/waves-h265.mp4' type='video/mp4' />
          <source data-src='/videos/waves-h264.mp4' type='video/mp4' />
        </chakra.video>
      </Box>
    </>
  );
}
